import { supabase } from "@/integrations/supabase/client";
import type { BlogPost } from "@/content/blog/posts";

type DbBlogPost = {
  id: string;
  slug: string;
  title: string;
  description: string;
  date: string;
  reading_minutes: number;
  tags: string[];
  category: string;
  cover_image: string;
  cover_alt: string;
  body: string;
  published: boolean;
  created_at: string;
  updated_at: string;
};

export const dbToPost = (r: DbBlogPost): BlogPost & { id: string; published: boolean } => ({
  id: r.id,
  slug: r.slug,
  title: r.title,
  description: r.description,
  date: r.date,
  readingMinutes: r.reading_minutes,
  tags: r.tags ?? [],
  category: r.category,
  coverImage: r.cover_image,
  coverAlt: r.cover_alt,
  body: r.body,
  published: r.published,
});

export async function listPosts(opts: { publishedOnly?: boolean } = {}) {
  let q = supabase.from("blog_posts").select("*").order("date", { ascending: false });
  if (opts.publishedOnly) q = q.eq("published", true);
  const { data, error } = await q;
  if (error) throw error;
  return (data as DbBlogPost[]).map(dbToPost);
}

export async function getPostBySlug(slug: string, opts: { publishedOnly?: boolean } = {}) {
  let q = supabase.from("blog_posts").select("*").eq("slug", slug).maybeSingle();
  const { data, error } = await q;
  if (error) throw error;
  if (!data) return null;
  if (opts.publishedOnly && !(data as DbBlogPost).published) return null;
  return dbToPost(data as DbBlogPost);
}

export async function getPostById(id: string) {
  const { data, error } = await supabase.from("blog_posts").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return data ? dbToPost(data as DbBlogPost) : null;
}

export type PostInput = {
  slug: string;
  title: string;
  description: string;
  date: string;
  reading_minutes: number;
  tags: string[];
  category: string;
  cover_image: string;
  cover_alt: string;
  body: string;
  published: boolean;
};

export async function createPost(input: PostInput) {
  const { data, error } = await supabase.from("blog_posts").insert(input).select().single();
  if (error) throw error;
  return data as DbBlogPost;
}

export async function updatePost(id: string, input: Partial<PostInput>) {
  const { data, error } = await supabase
    .from("blog_posts")
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as DbBlogPost;
}

export async function deletePost(id: string) {
  const { error } = await supabase.from("blog_posts").delete().eq("id", id);
  if (error) throw error;
}
